import { useTranslation } from "react-i18next";
import "./styles.scss";
import { CHART_LEGENDS } from "../constants";
import dayjs from "dayjs";
import SelectComponent from "components/select/Select";
import ButtonComponent from "components/button/Button";
import { apiExportRecordsCsvAuto, apiExportRecordsCsvManual } from "helpers/api/records";
import * as htmlToImage from "html-to-image";
import jsPDF from "jspdf";

const TableRow = ({
  width,
  data = [],
  title,
  bgColor,
  rowkey,
  t,
  chartDataWithDay,
  daysInMonth,
  treatmentDate,
  dateSelected,
}) => {
  const getDiffDate = (day) => {
    const treatmentDateConvert = dayjs(treatmentDate).format('YYYY/MM/DD')
    var diff = day.diff(treatmentDateConvert, 'd', true);
    if (diff < 0) {
      return 0
    }
    return diff + 1;
  }

  return (
    <div style={{ display: "flex" }}>
      <div className="table-row-title" style={{ background: bgColor }}>
        {title}
      </div>
      {[...new Array(daysInMonth)]?.map((it, idx) => {
        let rowData = data[idx] === undefined ? "" : data[idx];
        if (rowkey === "dayNumber" && typeof data[idx] === "number") {
          var day = `${dayjs(dateSelected).year()}/${dayjs(dateSelected).month() + 1}/${idx + 1}`
          rowData = getDiffDate(dayjs(day));
        }

        if (
          typeof data[idx] === "number" &&
          !["dayNumber", "day"].includes(rowkey)
        ) {
          rowData = data[idx] >= 0 ? data[idx].toFixed(1) : "";
        }

        if (rowkey === "day") {
          rowData = `${dayjs(dateSelected).month() + 1}/${idx + 1}`;
        }

        if (rowkey === "isBedWetting") {
          rowData =
            rowData === true
              ? t(`graph.yes`)
              : rowData === false
                ? t(`graph.no`)
                : "";
        }

        if (
          rowData &&
          ["sleepTime", "bedwettingTime", "toiletTime", "wakeUpTime"].includes(
            rowkey
          )
        ) {
          rowData = dayjs(rowData).format("HH:mm");
        }

        rowData = rowData || "-";

        if (
          dayjs(dateSelected).date() < idx + 1 &&
          !["dayNumber", "day"].includes(rowkey)
        ) {
          rowData = "";
        }

        return (
          <div
            key={idx}
            style={{
              borderRight: "none",
              width,
              fontSize: "10px",
              background:
                rowkey === "isBedWetting" && data[idx] === false ? "#ff0" : "",
            }}
            className="table-row-cell"
          >
            {rowData ? `${rowData}` : ""}
          </div>
        );
      })}
      <div
        style={{
          borderLeft: "2px solid black",
          borderRight: "2px solid black",
          width: `calc(100% - ${(width + 1) * daysInMonth}px - 133px)`,
        }}
        className="table-row-cell"
      ></div>
    </div>
  );
};

const Table = ({
  tableData = {},
  width,
  chartDataWithDay,
  daysInMonth,
  dateSelected,
  exportOption,
  exportType,
  setExportType,
  element,
  setIsLoading,
  treatmentDate,
  patientId
}) => {
  const { t } = useTranslation();

  const EXPORT_OPTIONS_VALUE = {
    PDF: 0,
    CSV_MANUAL: 1,
    CSV_AUTO: 2,
  };

  const handleExport = async () => {
    setIsLoading(true);
    switch (exportType) {
      case EXPORT_OPTIONS_VALUE.PDF:
        exportPDF();
        break;
      case EXPORT_OPTIONS_VALUE.CSV_MANUAL:
        exportCSV(exportType);
        break;
      case EXPORT_OPTIONS_VALUE.CSV_AUTO:
        exportCSV(exportType);
        break;
      default:
        setIsLoading(false);
        break;
    }
  };

  const exportPDF = async () => {
    if (element.current) {
      const elementChart = element.current
      const images1 = elementChart.querySelectorAll('svg image');
      images1.forEach((img) => {
        img.outerHTML = img.outerHTML.replace("xlink:", "")
      });
      const elementToExport = elementChart.cloneNode(true);
      const images = elementToExport.querySelectorAll('svg image');
      images.forEach((img) => {
        img.outerHTML = img.outerHTML.replace("xlink:", "")
      });
      elementToExport.style.height = 890;
      elementToExport.style.backgroundImage = 'url(/background_urine.png)';

      const table = elementToExport.getElementsByClassName('table-container')[0];
      table.removeChild(table.getElementsByClassName('list-legend')[0]);

      // Temp encluding element to export.
      const temp = document.createElement('div');
      temp.style.position = 'absolute';
      temp.style.left = '-9999px';

      // Logo
      const logo = document.createElement('div');
      logo.style.display = "flex";
      logo.style.flexDirection = "row";
      logo.style.alignItems = "center";
      logo.style.height = "60px";
      const img = document.createElement('img');
      img.src = "/icons/logo.webp";
      img.alt = "icon-logo";
      img.width = 200;
      img.height = 50;
      img.marginLeft = "20px";
      logo.appendChild(img);

      // Title in the PDF file.
      const title = document.createElement('h1');
      title.textContent = t("graph.exportPDF.title_urine");
      title.style.fontWeight = "600";
      title.style.fontSize = "1.8em";
      title.style.marginBottom = "0";

      // Time period.
      const timeString = document.createElement('span');
      timeString.textContent = `${t("graph.exportPDF.timeStr")}${dateSelected.format(t("graph.chartTime"))}（${dayjs(dateSelected).startOf('month').format('YYYY年MM月DD日')}～${dayjs(dateSelected).endOf('month').format('YYYY年MM月DD日')}）`;
      timeString.style.fontSize = "1.2em";

      // Copyright.
      const copyright = document.createElement('div');
      copyright.textContent = t("graph.exportPDF.copyright");
      copyright.style.textAlign = "right";
      copyright.style.fontSize = "0.6em";
      copyright.style.position = "absolute";
      copyright.style.top = "890px";
      copyright.style.right = "20px";

      const PDFHeader = document.createElement('div');
      PDFHeader.style.textAlign = "center";
      PDFHeader.style.padding = '10px';
      PDFHeader.style.margin = '5px 0';
      PDFHeader.appendChild(title);
      PDFHeader.appendChild(timeString);

      elementToExport.style.padding = "10px";
      elementToExport.insertBefore(PDFHeader, elementToExport.firstChild);
      elementToExport.insertBefore(logo, elementToExport.firstChild);
      elementToExport.append(copyright, elementToExport.lastChild);

      // Custom style for chart
      const charts = elementToExport.getElementsByClassName('highcharts-background');
      // const plotCharts = elementToExport.getElementsByClassName('highcharts-plot-background');
      for (const el of charts) {
        el.style.fill = "url(/icons/logo.webp)";
        // el.style.backgroundImage = "url(/icons/logo.webp)";
      }

      temp.appendChild(elementToExport);
      document.body.appendChild(temp);

      const imgData = await htmlToImage.toPng(elementToExport);
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'px'
      });

      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${t("graph.exportPDF.fileNameBedWetting")}.pdf`);

      // clean up the temp
      document.body.removeChild(temp);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }

  const exportCSV = async (type) => {
    const from = dayjs(dateSelected)
      .startOf("month")
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD hh:mm");
    const to = dayjs(dateSelected)
      .endOf("month")
      .endOf("day")
      .utc()
      .format("YYYY-MM-DD hh:mm");

    let res = {};

    switch (type) {
      case EXPORT_OPTIONS_VALUE.CSV_MANUAL:
        res = await apiExportRecordsCsvManual({ from, to, patient_id: patientId });
        break;
      case EXPORT_OPTIONS_VALUE.CSV_AUTO:
        res = await apiExportRecordsCsvAuto({ from, to, patient_id: patientId });
        break;
      default:
        break;
    }

    if (res.data) {
      window.location.href = res.data;
    }
    setIsLoading(false);
    return false;
  }

  return (
    <div
      style={{
        borderSpacing: 0,
        // marginLeft: "27px",
        background: "white",
        tableLayout: "fixed",
      }}
      className="table-container"
    >
      {tableData &&
        Object.keys(tableData).map((rowkey, idx) => {
          const item = tableData[rowkey];

          return (
            <TableRow
              t={t}
              rowkey={rowkey}
              title={item.title}
              key={rowkey}
              width={width}
              data={item.data}
              bgColor={item.bgColor}
              treatmentDate={treatmentDate}
              chartDataWithDay={chartDataWithDay}
              daysInMonth={daysInMonth}
              dateSelected={dateSelected}
            />
          );
        })}
      <div className="list-legend">
        {CHART_LEGENDS.map((item, index) => {
          return (
            <div className="chart-legend" key={index}>
              <img
                src={item.icon}
                alt=""

              // height="20px"
              />
              {t(item.title)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Table;
